import { useEffect, useRef } from 'react';
import useClassificationStore from '../store/useClassificationStore';

/**
 * Keyboard-first labeling for the classification studio.
 *
 *   1-9, 0   assign the previewed image to that class, then jump to the next
 *   A / D, arrows   walk the unlabeled queue
 *   Ctrl/Cmd+A      select every image in the current grid
 *   Delete   remove the previewed image (or the whole multi-selection)
 *   Esc      clear a multi-selection
 *
 * Classification is a high-volume, low-decision task: routing every single
 * image through a mouse click is the slow path, so the digits mirror the class
 * bar and the queue advances on its own.
 *
 * When several images are multi-selected, a digit assigns all of them — the
 * same rule the class bar itself follows.
 */

/**
 * SweetAlert dialogs focus a button rather than an input, so without this a
 * digit would assign a class behind an open confirm dialog.
 */
const isDialogOpen = () => !!document.querySelector('.swal2-container');

const isTypingTarget = (element) =>
  !!element &&
  (element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    element.tagName === 'SELECT' ||
    element.isContentEditable);

/** '1'..'9' -> 0..8 and '0' -> 9, so the digits read left to right. */
const digitToClassIndex = (key) => {
  if (!/^[0-9]$/.test(key)) return -1;
  return key === '0' ? 9 : Number(key) - 1;
};

const useClassificationShortcuts = (handlers) => {
  const { classes, unstructuredImages, selectedImage, selectedImages, setSelectedImage } =
    useClassificationStore();

  // the hook rebuilds its handlers every render; read them through a ref so the
  // listener is not torn down and re-attached on each one
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const step = (direction) => {
      if (unstructuredImages.length === 0) return;
      const current = unstructuredImages.findIndex((img) => img.id === selectedImage?.id);
      // not in the queue (an already-classified image is previewed): start at the top
      const next =
        current === -1
          ? 0
          : (current + direction + unstructuredImages.length) % unstructuredImages.length;
      setSelectedImage(unstructuredImages[next]);
    };

    const onKeyDown = (event) => {
      if (isTypingTarget(event.target) || isDialogOpen()) return;

      // Ctrl/Cmd+A must be handled BEFORE the modifier guard below, and it has to
      // preventDefault or the browser selects the page text instead.
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        handlersRef.current.selectAllUnstructured();
        return;
      }

      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const classIndex = digitToClassIndex(event.key);
      if (classIndex >= 0) {
        const target = classes[classIndex];
        if (!target) return;
        event.preventDefault();
        if (selectedImages.size > 1) {
          handlersRef.current.assignMultipleToClass(target.id);
        } else if (selectedImage) {
          handlersRef.current.handleClassButtonClick(target.id);
        }
        return;
      }

      const key = event.key.toLowerCase();

      if (event.key === 'ArrowRight' || key === 'd') {
        event.preventDefault();
        step(1);
        return;
      }

      if (event.key === 'ArrowLeft' || key === 'a') {
        event.preventDefault();
        step(-1);
        return;
      }

      if (event.key === 'Delete') {
        // A multi-selection is what the user is looking at, so Delete acts on it —
        // the same rule the class chips and the bulk bar's trash button follow.
        if (selectedImages.size > 1) {
          event.preventDefault();
          handlersRef.current.deleteSelectedImages();
          return;
        }
        if (!selectedImage) return;
        event.preventDefault();
        handlersRef.current.deleteImage(selectedImage.id, event);
        return;
      }

      if (event.key === 'Escape' && selectedImages.size > 0) {
        event.preventDefault();
        handlersRef.current.clearSelection();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [classes, unstructuredImages, selectedImage, selectedImages, setSelectedImage]);
};

export default useClassificationShortcuts;
